import { useState, FC } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import useIsManagerView from 'screens/performanceReview/hooks/useIsManagerView';

import { ChevronRight } from 'assets/assetsV1/iconComponents';

import { BottomDrawer, DrawerBody, DrawerContent, DrawerHeader } from 'uiToolkitV1/BottomDrawer';
import { Button, BUTTON_VARIANT } from 'uiToolkitV1/Button';
import { TextLabel } from 'uiToolkitV1/Typography';

import PeerFeedbackRenderer from '../PeerFeedback/PeerFeedbackRenderer';
import usePeerFeedbackData from '../PeerFeedback/hooks/dataHooks';

interface ReviewFeedbackTypeDrawerProps {
  isOpen: boolean;
  handleClose: () => void;
  question: { id: string };
  selfFeedback: {
    employee?: unknown;
    feedbackTextHtml?: string;
    feedbackText?: string;
    providerType?: string;
    selectedAnswerOptions?: unknown[];
  } | null;
}

const ReviewFeedbackTypeDrawer: FC<ReviewFeedbackTypeDrawerProps> = ({
  isOpen,
  handleClose,
  question,
  selfFeedback,
}) => {
  const { employee, feedbackTextHtml, feedbackText, providerType, selectedAnswerOptions } = selfFeedback ?? {};

  return (
    <BottomDrawer isOpen={isOpen} onClose={handleClose}>
      <DrawerContent width="90%" marginRight="auto">
        <DrawerHeader onClose={handleClose} left="-7px">
          {null}
        </DrawerHeader>
        <DrawerBody>
          <Flex borderRadius="8px" h="100%" bg="surface.EBEFF3" p="1" pb={0} mx={1}>
            <Flex
              display="flex"
              padding="8px 8px 12px 8px"
              flexDirection="column"
              alignItems="flex-start"
              gap="12px"
              flexShrink={0}
              borderRadius="8px"
              background="var(--White, #FFF)"
              width="100%"
            >
              <Box bg="#F5F7F9" p="16px" display="flex" flexDir="column" width="100%">
                <Flex flexDir="column">
                  <PeerFeedbackRenderer
                    employee={employee}
                    feedbackTextHtml={feedbackTextHtml}
                    feedbackText={feedbackText}
                    providerType={providerType}
                    selectedAnswerOptions={selectedAnswerOptions}
                    question={question}
                  />
                </Flex>
              </Box>
            </Flex>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </BottomDrawer>
  );
};

interface ReviewFeedbackTypeLabelProps {
  label: string;
  question: { id: string };
  goalId: string;
  labelComp: FC | null;
}

export const ReviewFeedbackTypeLabel: FC<ReviewFeedbackTypeLabelProps> = ({
  label,
  question,
  goalId,
  labelComp: LabelComp,
}) => {
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const { id: questionId } = question;
  const { isManagerView } = useIsManagerView();

  // @ts-expect-error competencyId is expected
  const { data = [] } = usePeerFeedbackData({
    goalId,
    questionId,
    isManagerView,
  });

  const selfFeedback = data?.find((feedback) => feedback?.providerType === 'SELF');

  return (
    <Flex justifyContent="space-between" alignItems="center" mb="4px">
      {LabelComp ? (
        <LabelComp />
      ) : (
        <TextLabel bold color="text.g2" lineHeight="20px" fontSize="14px">
          {label}
        </TextLabel>
      )}
      {selfFeedback && (
        <Button
          variant={BUTTON_VARIANT.tertiary}
          EndIcon={ChevronRight}
          color="text.g3"
          onClick={() => setDrawerOpen(true)}
          padding="0px !important"
          endIconProps={{ height: '20px', width: '20px' }}
        >
          {selfFeedback?.employee?.displayName}&apos;s self review
        </Button>
      )}
      <ReviewFeedbackTypeDrawer
        isOpen={isDrawerOpen}
        handleClose={() => setDrawerOpen(false)}
        question={question}
        selfFeedback={selfFeedback}
      />
    </Flex>
  );
};
